(() => {
  if (window.InkNfiberArtworkCostEstimator) return;

  const defaults = {
    enabled: true,
    currency: 'USD',
    filmPricePer100m: 129,
    whiteInkPricePer500ml: 39,
    colorInkPricePer500ml: 35,
    filmWidthInches: 24,
    filmWastePercent: 8,
    whiteInkMlPerSqIn: 0.045,
    colorInkMlPerSqIn: 0.005,
    laborCost: 0.75,
    laborSetupCents: 150,
    areaRatePerSqIn: 0.01,
    minPrintCharge: 3.5,
    minimumChargeCents: 350,
    largeAreaThresholdSqIn: 100,
    largeAreaMultiplier: 1.15,
    wasteMultiplierPercent: 125,
    profitMultiplierPercent: 200,
    lowComplexityMultiplierPercent: 100,
    mediumComplexityMultiplierPercent: 105,
    highComplexityMultiplierPercent: 110,
    enableAreaRateFallback: false,
    profitMode: 'fixed',
    fixedProfit: 3,
    marginPercent: 60,
    showInternalCost: false,
    showSuggestedPrice: true,
    debug: false,
    checkoutContext: 'generic',
  };
  const globalDebug = new URLSearchParams(window.location.search).get('artwork_estimator_debug') === '1';

  const parseNumber = (value, fallback) => {
    const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseBool = (value, fallback = false) => {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return fallback;
  };

  const normalizeProfitMode = (value) => (String(value || '').toLowerCase() === 'margin' ? 'margin' : 'fixed');
  const readDataset = (dataset, ...keys) => {
    for (const key of keys) {
      if (dataset && dataset[key] !== undefined) return dataset[key];
    }
    return undefined;
  };

  function configFromDataset(dataset = {}, fixedProfitDefault = defaults.fixedProfit) {
    return {
      enabled: parseBool(dataset.artworkEstimatorEnabled, defaults.enabled),
      currency: String(dataset.artworkEstimatorCurrency || defaults.currency),
      filmPricePer100m: parseNumber(readDataset(dataset, 'artworkEstimatorFilmPricePer100m', 'artworkEstimatorFilmPricePer-100m'), defaults.filmPricePer100m),
      whiteInkPricePer500ml: parseNumber(readDataset(dataset, 'artworkEstimatorWhiteInkPricePer500ml', 'artworkEstimatorWhiteInkPricePer-500ml'), defaults.whiteInkPricePer500ml),
      colorInkPricePer500ml: parseNumber(readDataset(dataset, 'artworkEstimatorColorInkPricePer500ml', 'artworkEstimatorColorInkPricePer-500ml'), defaults.colorInkPricePer500ml),
      filmWidthInches: parseNumber(dataset.artworkEstimatorFilmWidthInches, defaults.filmWidthInches),
      filmWastePercent: parseNumber(dataset.artworkEstimatorFilmWastePercent, defaults.filmWastePercent),
      whiteInkMlPerSqIn: parseNumber(dataset.artworkEstimatorWhiteInkMlPerSqIn, defaults.whiteInkMlPerSqIn),
      colorInkMlPerSqIn: parseNumber(dataset.artworkEstimatorColorInkMlPerSqIn, defaults.colorInkMlPerSqIn),
      laborCost: parseNumber(dataset.artworkEstimatorLaborCost, defaults.laborCost),
      laborSetupCents: parseNumber(dataset.artworkEstimatorLaborSetupCents, defaults.laborSetupCents),
      areaRatePerSqIn: dataset.artworkEstimatorAreaRateCentsPerSqIn !== undefined
        ? parseNumber(dataset.artworkEstimatorAreaRateCentsPerSqIn, defaults.areaRatePerSqIn * 100) / 100
        : parseNumber(dataset.artworkEstimatorAreaRatePerSqIn, defaults.areaRatePerSqIn),
      enableAreaRateFallback: parseBool(dataset.artworkEstimatorEnableAreaRateFallback, defaults.enableAreaRateFallback),
      minPrintCharge: dataset.artworkEstimatorMinPrintChargeCents !== undefined
        ? parseNumber(dataset.artworkEstimatorMinPrintChargeCents, defaults.minPrintCharge * 100) / 100
        : parseNumber(dataset.artworkEstimatorMinPrintCharge, defaults.minPrintCharge),
      minimumChargeCents: parseNumber(dataset.artworkEstimatorMinimumChargeCents, defaults.minimumChargeCents),
      largeAreaThresholdSqIn: parseNumber(dataset.artworkEstimatorLargeAreaThresholdSqIn, defaults.largeAreaThresholdSqIn),
      largeAreaMultiplier: dataset.artworkEstimatorLargeAreaMultiplierPercent !== undefined
        ? parseNumber(dataset.artworkEstimatorLargeAreaMultiplierPercent, defaults.largeAreaMultiplier * 100) / 100
        : parseNumber(dataset.artworkEstimatorLargeAreaMultiplier, defaults.largeAreaMultiplier),
      wasteMultiplierPercent: parseNumber(dataset.artworkEstimatorWasteMultiplierPercent, defaults.wasteMultiplierPercent),
      profitMultiplierPercent: parseNumber(dataset.artworkEstimatorProfitMultiplierPercent, defaults.profitMultiplierPercent),
      lowComplexityMultiplierPercent: parseNumber(dataset.artworkEstimatorLowComplexityMultiplierPercent, defaults.lowComplexityMultiplierPercent),
      mediumComplexityMultiplierPercent: parseNumber(dataset.artworkEstimatorMediumComplexityMultiplierPercent, defaults.mediumComplexityMultiplierPercent),
      highComplexityMultiplierPercent: parseNumber(dataset.artworkEstimatorHighComplexityMultiplierPercent, defaults.highComplexityMultiplierPercent),
      profitMode: normalizeProfitMode(dataset.artworkEstimatorProfitMode),
      fixedProfit: parseNumber(dataset.artworkEstimatorFixedProfit, fixedProfitDefault),
      marginPercent: parseNumber(dataset.artworkEstimatorMarginPercent, defaults.marginPercent),
      showInternalCost: parseBool(dataset.artworkEstimatorShowInternalCost, defaults.showInternalCost),
      showSuggestedPrice: parseBool(dataset.artworkEstimatorShowSuggestedPrice, defaults.showSuggestedPrice),
      debug: globalDebug || parseBool(dataset.debug, defaults.debug),
      checkoutContext: String(dataset.artworkEstimatorCheckoutContext || defaults.checkoutContext),
    };
  }

  function formatMoney(value, currency = defaults.currency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || defaults.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  }

  function round(value, places = 2) {
    const factor = 10 ** places;
    return Math.round(Number(value || 0) * factor) / factor;
  }

  function roundToQuarter(value) {
    return Math.round(Number(value || 0) * 4) / 4;
  }

  function complexityLabel(colorCount, coveragePercent) {
    if (coveragePercent >= 65 || (colorCount > 48 && coveragePercent >= 35)) return { label: 'High', multiplier: 1.1 };
    if (colorCount > 12 || coveragePercent >= 35) return { label: 'Medium', multiplier: 1.05 };
    return { label: 'Low', multiplier: 1 };
  }

  function fallbackAnalysis() {
    return {
      coveragePercent: 100,
      colorCount: 1,
      complexity: 'Medium',
      complexityMultiplier: 1.05,
      isFallback: true,
    };
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });
  }

  function isNearWhite(red, green, blue) {
    return red >= 245 && green >= 245 && blue >= 245 && Math.max(red, green, blue) - Math.min(red, green, blue) <= 10;
  }

  function prepareImageCanvas(image, width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(Number(width) || image?.naturalWidth || image?.width || 1));
    canvas.height = Math.max(1, Math.round(Number(height) || image?.naturalHeight || image?.height || 1));
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context || !image) return canvas;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    try {
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      let transparentPixels = 0;
      for (let index = 3; index < pixels.length; index += 4) {
        if (pixels[index] <= 10) transparentPixels += 1;
      }

      const hasTransparency = transparentPixels > canvas.width * canvas.height * 0.01;
      if (!hasTransparency) {
        for (let index = 0; index < pixels.length; index += 4) {
          if (isNearWhite(pixels[index], pixels[index + 1], pixels[index + 2])) {
            pixels[index + 3] = 0;
          }
        }
        context.putImageData(imageData, 0, 0);
      }
    } catch (_error) {
      // Cross-origin images without pixel access remain drawable; callers can use their geometry fallback.
    }

    return canvas;
  }

  function analyzeCanvas(sourceCanvas, options = {}) {
    if (!sourceCanvas) return fallbackAnalysis();

    try {
      const sourceWidth = sourceCanvas.width || 0;
      const sourceHeight = sourceCanvas.height || 0;
      if (!sourceWidth || !sourceHeight) return fallbackAnalysis();

      const maxSide = Math.max(32, parseNumber(options.maxSide, 128));
      const cropToVisible = parseBool(options.cropToVisible, false);
      const scale = Math.min(1, maxSide / Math.max(sourceWidth, sourceHeight));
      const width = Math.max(1, Math.round(sourceWidth * scale));
      const height = Math.max(1, Math.round(sourceHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return fallbackAnalysis();
      context.drawImage(sourceCanvas, 0, 0, width, height);
      const pixels = context.getImageData(0, 0, width, height).data;
      const colors = new Set();
      let visiblePixels = 0;
      let transparentPixels = 0;
      let minX = width;
      let minY = height;
      let maxX = -1;
      let maxY = -1;

      for (let index = 0; index < pixels.length; index += 4) {
        const alpha = pixels[index + 3];
        if (alpha <= 10) transparentPixels += 1;
      }

      const totalPixels = width * height;
      const hasTransparency = transparentPixels > totalPixels * 0.01;

      for (let index = 0; index < pixels.length; index += 4) {
        const red = pixels[index];
        const green = pixels[index + 1];
        const blue = pixels[index + 2];
        const alpha = pixels[index + 3];
        if (hasTransparency && alpha <= 10) continue;
        if (!hasTransparency && isNearWhite(red, green, blue)) continue;
        const pixelIndex = index / 4;
        const x = pixelIndex % width;
        const y = Math.floor(pixelIndex / width);
        visiblePixels += 1;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
        colors.add(`${red >> 5},${green >> 5},${blue >> 5}`);
      }

      const hasVisiblePixels = visiblePixels > 0 && maxX >= minX && maxY >= minY;
      const boundsWidth = hasVisiblePixels ? maxX - minX + 1 : 0;
      const boundsHeight = hasVisiblePixels ? maxY - minY + 1 : 0;
      const coveragePixelCount = cropToVisible && hasVisiblePixels
        ? boundsWidth * boundsHeight
        : totalPixels;
      const coveragePercent = coveragePixelCount ? (visiblePixels / coveragePixelCount) * 100 : 0;
      const colorCount = Math.max(1, colors.size);
      const complexity = complexityLabel(colorCount, coveragePercent);

      return {
        coveragePercent: round(coveragePercent, 1),
        colorCount,
        complexity: complexity.label,
        complexityMultiplier: complexity.multiplier,
        isFallback: false,
        isEmpty: !hasVisiblePixels,
        bounds: hasVisiblePixels
          ? {
              xRatio: minX / width,
              yRatio: minY / height,
              widthRatio: boundsWidth / width,
              heightRatio: boundsHeight / height,
            }
          : null,
      };
    } catch (_error) {
      return fallbackAnalysis();
    }
  }

  async function analyzeImage(src) {
    if (!src) return fallbackAnalysis();

    try {
      const image = await loadImage(src);
      const sourceWidth = image.naturalWidth || image.width || 0;
      const sourceHeight = image.naturalHeight || image.height || 0;
      if (!sourceWidth || !sourceHeight) return fallbackAnalysis();

      const maxSide = 128;
      const scale = Math.min(1, maxSide / Math.max(sourceWidth, sourceHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(sourceWidth * scale));
      canvas.height = Math.max(1, Math.round(sourceHeight * scale));
      const context = canvas.getContext('2d');
      if (!context) return fallbackAnalysis();
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return analyzeCanvas(canvas, { maxSide });
    } catch (_error) {
      return fallbackAnalysis();
    }
  }

  function calculate(input = {}) {
    const config = { ...defaults, ...(input.config || {}) };
    const widthInches = Math.max(0, parseNumber(input.widthInches, 0));
    const heightInches = Math.max(0, parseNumber(input.heightInches, 0));
    const areaSqIn = widthInches * heightInches;
    const analysis = input.analysis || fallbackAnalysis();
    const coverageDecimal = Math.max(0, Math.min(1, parseNumber(analysis.coveragePercent, 100) / 100));
    const filmCostPerMeter = config.filmPricePer100m / 100;
    const whiteInkCostPerMl = config.whiteInkPricePer500ml / 500;
    const colorInkCostPerMl = config.colorInkPricePer500ml / 500;
    const filmLengthMeters = heightInches * 0.0254;
    const filmCost = filmLengthMeters * filmCostPerMeter;
    const filmCostWithWaste = filmCost * (1 + Math.max(0, config.filmWastePercent) / 100);
    const whiteInkMl = areaSqIn * coverageDecimal * config.whiteInkMlPerSqIn;
    const colorInkMl = areaSqIn * coverageDecimal * config.colorInkMlPerSqIn;
    const whiteInkCost = whiteInkMl * whiteInkCostPerMl;
    const colorInkCost = colorInkMl * colorInkCostPerMl;
    const materialCost = filmCostWithWaste + whiteInkCost + colorInkCost;
    const areaChargeBase = areaSqIn * Math.max(0, config.areaRatePerSqIn);
    const fallbackAreaMultiplier = areaSqIn >= Math.max(0, config.largeAreaThresholdSqIn)
      ? Math.max(1, config.largeAreaMultiplier)
      : 1;
    const areaCharge = config.enableAreaRateFallback && analysis.isFallback ? areaChargeBase * fallbackAreaMultiplier : 0;
    const rawMaterialCost = Math.max(materialCost, areaCharge);
    const wasteMultiplier = Math.max(1, config.wasteMultiplierPercent / 100);
    const laborSetupCost = Math.max(0, config.laborSetupCents / 100);
    const businessCost = (rawMaterialCost * wasteMultiplier) + laborSetupCost;
    const profitMultiplier = Math.max(1, config.profitMultiplierPercent / 100);
    const complexityMultiplierByLabel = {
      Low: Math.max(1, config.lowComplexityMultiplierPercent / 100),
      Medium: Math.max(1, config.mediumComplexityMultiplierPercent / 100),
      High: Math.max(1, config.highComplexityMultiplierPercent / 100),
    };
    const complexityMultiplier = complexityMultiplierByLabel[analysis.complexity] || Math.max(1, analysis.complexityMultiplier || 1);
    const minimumCharge = Math.max(
      0,
      config.minimumChargeCents !== undefined
        ? config.minimumChargeCents / 100
        : config.minPrintCharge
    );
    const suggestedPrice = Math.max(minimumCharge, businessCost * profitMultiplier) * complexityMultiplier;
    const productionCost = businessCost;
    const result = {
      widthInches: round(widthInches, 2),
      heightInches: round(heightInches, 2),
      squareInches: round(areaSqIn, 2),
      coveragePercent: round(analysis.coveragePercent, 1),
      colorCount: analysis.colorCount || 1,
      complexity: analysis.complexity || 'Medium',
      filmLengthMeters: round(filmLengthMeters, 4),
      filmCost: round(filmCostWithWaste, 4),
      whiteInkMl: round(whiteInkMl, 4),
      colorInkMl: round(colorInkMl, 4),
      whiteInkCost: round(whiteInkCost, 4),
      colorInkCost: round(colorInkCost, 4),
      materialCost: round(rawMaterialCost, 4),
      rawMaterialCost: round(rawMaterialCost, 4),
      areaCharge: round(areaCharge, 2),
      laborCost: round(laborSetupCost, 4),
      laborSetupCost: round(laborSetupCost, 4),
      wasteMultiplierPercent: round(config.wasteMultiplierPercent, 2),
      profitMultiplierPercent: round(config.profitMultiplierPercent, 2),
      complexityMultiplier: round(complexityMultiplier, 2),
      businessCost: round(businessCost, 2),
      productionCost: round(productionCost, 2),
      suggestedPrice: roundToQuarter(suggestedPrice),
      profitMode: config.profitMode,
      fixedProfitAmount: round(config.fixedProfit, 2),
      marginPercent: round(config.marginPercent, 2),
      currency: config.currency,
    };

    if (config.debug) {
      console.debug('[InkNfiber artwork estimator]', {
        widthInches: result.widthInches,
        heightInches: result.heightInches,
        areaSqIn: result.squareInches,
        coveragePercent: result.coveragePercent,
        colorCount: result.colorCount,
        complexity: result.complexity,
        filmCost: result.filmCost,
        whiteInkCost: result.whiteInkCost,
        colorInkCost: result.colorInkCost,
        fallbackAreaCharge: result.areaCharge,
        laborHandlingCost: result.laborCost,
        rawMaterialCost: result.rawMaterialCost,
        businessCost: result.businessCost,
        wasteMultiplierPercent: result.wasteMultiplierPercent,
        profitMultiplierPercent: result.profitMultiplierPercent,
        complexityMultiplier: result.complexityMultiplier,
        suggestedPrice: result.suggestedPrice,
      });
    }

    return result;
  }

  function disclaimerForContext(context) {
    if (context === 'custom-designs') return 'Reference only. Apparel checkout pricing is unchanged.';
    if (context === 'dtf-film') return 'Production estimate only. Checkout price is based on DTF film sheet size.';
    if (context === 'dtf-transfers-by-size') return 'Reference only. Checkout price is based on the selected transfer size and quantity tiers.';
    return 'Reference only. Checkout price is unchanged.';
  }

  function estimateMarkup(estimate, config = {}) {
    const settings = { ...defaults, ...config };
    if (!settings.enabled) return '';
    if (!estimate) {
      return '<p class="inknfiber-artwork-estimate__empty">Upload artwork to calculate a reference estimate.</p>';
    }

    const customerRows = [
      `<div><dt>Artwork size</dt><dd>${estimate.widthInches}" x ${estimate.heightInches}"</dd></div>`,
      `<div><dt>Area</dt><dd>${estimate.squareInches} sq in</dd></div>`,
      `<div><dt>Ink coverage</dt><dd>${estimate.coveragePercent}%</dd></div>`,
      `<div><dt>Estimated colors</dt><dd>${estimate.colorCount}</dd></div>`,
      `<div><dt>Color complexity</dt><dd>${estimate.complexity}</dd></div>`,
    ];
    const suggestedPriceLabel = settings.checkoutContext === 'dtf-film' || settings.checkoutContext === 'custom-designs'
      ? 'Production price reference'
      : 'Suggested retail price';
    const priceMarkup = settings.showSuggestedPrice
      ? `
        <div class="inknfiber-artwork-estimate__price">
          <span>${suggestedPriceLabel}</span>
          <strong>${formatMoney(estimate.suggestedPrice, estimate.currency)}</strong>
        </div>
      `
      : '';
    const internalMarkup = settings.showInternalCost
      ? `
        <div class="inknfiber-artwork-estimate__internal">
          <h4>Internal cost breakdown</h4>
          <dl>
            <div><dt>Film used</dt><dd>${formatMoney(estimate.filmCost, estimate.currency)}</dd></div>
            <div><dt>White ink</dt><dd>${formatMoney(estimate.whiteInkCost, estimate.currency)}</dd></div>
            <div><dt>Color ink</dt><dd>${formatMoney(estimate.colorInkCost, estimate.currency)}</dd></div>
            <div><dt>Raw material cost</dt><dd>${formatMoney(estimate.rawMaterialCost, estimate.currency)}</dd></div>
            <div><dt>Labor / setup</dt><dd>${formatMoney(estimate.laborSetupCost, estimate.currency)}</dd></div>
            <div><dt>Business cost after waste</dt><dd>${formatMoney(estimate.businessCost, estimate.currency)}</dd></div>
            <div><dt>Profit multiplier</dt><dd>${estimate.profitMultiplierPercent}%</dd></div>
            <div><dt>Complexity multiplier</dt><dd>${estimate.complexityMultiplier}x</dd></div>
          </dl>
        </div>
      `
      : '';

    return `
      <dl class="inknfiber-artwork-estimate__rows">${customerRows.join('')}</dl>
      ${internalMarkup}
      ${priceMarkup}
      <p class="inknfiber-artwork-estimate__disclaimer">${disclaimerForContext(settings.checkoutContext)}</p>
    `;
  }

  function metadata(estimate) {
    if (!estimate) return {};
    return {
      '_artwork_estimate_width_in': String(estimate.widthInches),
      '_artwork_estimate_height_in': String(estimate.heightInches),
      '_artwork_estimate_square_inches': String(estimate.squareInches),
      '_artwork_estimate_coverage_percent': String(estimate.coveragePercent),
      '_artwork_estimate_color_count': String(estimate.colorCount),
      '_artwork_estimate_complexity': estimate.complexity,
      '_artwork_estimate_production_cost': formatMoney(estimate.productionCost, estimate.currency),
      '_artwork_estimate_suggested_price': formatMoney(estimate.suggestedPrice, estimate.currency),
    };
  }

  window.InkNfiberArtworkCostEstimator = {
    defaults,
    configFromDataset,
    prepareImageCanvas,
    analyzeCanvas,
    analyzeImage,
    calculate,
    estimateMarkup,
    metadata,
    formatMoney,
  };
})();
